import type { ResultOf } from '@graphql-typed-document-node/core';

import { graphql } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useGraphQLQuery } from '../../hooks/useGraphQLQuery.hook';

import { NoData } from '../Illustrations/NoData';
import { Loading } from '../Loading';
import { DataGrid } from '../DataGrid';

import { getParticipationColumns } from './columns';

interface Props {
  readonly thirdPartyId: Id;
}

export const ParticipationsCmp_OneThirdParty_QueryDocument = graphql(`
  query ParticipationsCmp_OneThirdParty_Query($id: Id!) {
    findOneThirdParty(id: $id) {
      _id
      kind
      typeId
      participations {
        _id
        mission {
          _id
          folderId
          code
          consolidation {
            internalActors {
              __typename
              isInternal
              _id
              thirdParty {
                __typename
                _id
                kind
                typeId
                ... on Organization {
                  _id
                  identifier {
                    kind
                    value
                  }
                  name
                  tradingName
                }
                ... on Person {
                  _id
                  civilityId
                  fullName
                }
              }
              kind
              typeId
              categoryIds
              ... on ActorOrganization {
                representatives {
                  _id
                  typeIds
                  person {
                    _id
                    civilityId
                    fullName
                  }
                }
              }
            }
            externalActors {
              __typename
              isInternal
              _id
              thirdParty {
                __typename
                _id
                kind
                typeId
                ... on Organization {
                  _id
                  identifier {
                    kind
                    value
                  }
                  name
                  tradingName
                }
                ... on Person {
                  _id
                  civilityId
                  fullName
                }
              }
              kind
              typeId
              categoryIds
              ... on ActorOrganization {
                representatives {
                  _id
                  typeIds
                  person {
                    _id
                    civilityId
                    fullName
                  }
                }
              }
            }
          }
        }
        actorId
        representativeId
      }
    }
  }
`);

export type ParticipationsCmp_OneThirdParty_Participation = ResultOf<
  typeof ParticipationsCmp_OneThirdParty_QueryDocument
>['findOneThirdParty']['participations'][number];

export const Participations = ({ thirdPartyId }: Readonly<Props>) => {
  const { civilities, actorCategories, representativeTypes } =
    useReferencesContext();

  const { data, isSuccess } = useGraphQLQuery(
    ParticipationsCmp_OneThirdParty_QueryDocument,
    { id: thirdPartyId },
    { enabled: !!thirdPartyId },
  );

  const kind = data?.findOneThirdParty.kind;
  const participations = data?.findOneThirdParty.participations;

  if (isSuccess && !participations?.length) {
    return <NoData />;
  }

  return (
    <>
      {!isSuccess && <Loading />}
      <DataGrid
        columns={getParticipationColumns({
          civilities,
          actorCategories,
          representativeTypes,
          kind,
        })}
        loading={!isSuccess}
        rows={participations ?? EMPTY_ARRAY}
      />
    </>
  );
};
